import { MealTime } from "@/clients/models/MealTime";
import { Plan } from "@/clients/models/Plan";
import { getPlanDayEntryByDateKey } from "@/features/meal-plan/utils/plan-week-data";

export type ProteinStatusId = "goodStart" | "onTrack" | "aLittleBehind" | "targetHit";

export interface ProteinStatus {
  id: ProteinStatusId;
  label: string;
  message: string;
  tone: "positive" | "attention";
}

interface GetProteinStatusInput {
  consumedProtein: number;
  targetProtein: number;
  loggedMainMealCount: number;
  localHour: number;
}

export interface LoggedProteinSummary {
  consumedProtein: number;
  loggedMainMealCount: number;
}

const MAIN_MEAL_TIMES = new Set<MealTime>([MealTime.BREAKFAST, MealTime.LUNCH, MealTime.DINNER]);

const MEANINGFUL_GAP_PERCENTAGE_POINTS = 15;

const getTimeExpectedProgress = (localHour: number): number => {
  if (localHour < 12) return 0;
  if (localHour < 15) return 30;
  if (localHour < 19) return 55;
  return 80;
};

const getMealExpectedProgress = (loggedMainMealCount: number): number => {
  if (loggedMainMealCount <= 0) return 0;
  if (loggedMainMealCount === 1) return 25;
  if (loggedMainMealCount === 2) return 55;
  return 80;
};

const getGoodStartStatus = (loggedMainMealCount: number): ProteinStatus => ({
  id: "goodStart",
  label: loggedMainMealCount === 0 ? "Ready when you are" : "Good start",
  message:
    loggedMainMealCount === 0
      ? "Log your first meal to start tracking your protein"
      : "Keep adding protein through the day",
  tone: "positive",
});

export const getProteinStatus = ({
  consumedProtein,
  targetProtein,
  loggedMainMealCount,
  localHour,
}: GetProteinStatusInput): ProteinStatus => {
  if (targetProtein <= 0) {
    return getGoodStartStatus(loggedMainMealCount);
  }

  const progressPercent = (Math.max(0, consumedProtein) / targetProtein) * 100;

  if (progressPercent >= 100) {
    return {
      id: "targetHit",
      label: "Target hit",
      message: "You’ve hit your protein target",
      tone: "positive",
    };
  }

  const isGoodStartPhase = localHour < 12 || (localHour < 15 && loggedMainMealCount <= 1);
  if (isGoodStartPhase) {
    return getGoodStartStatus(loggedMainMealCount);
  }

  const expectedProgress = Math.max(
    getTimeExpectedProgress(localHour),
    getMealExpectedProgress(loggedMainMealCount),
  );

  if (expectedProgress - progressPercent >= MEANINGFUL_GAP_PERCENTAGE_POINTS) {
    return {
      id: "aLittleBehind",
      label: "A little behind",
      message: "A protein-rich next meal would help",
      tone: "attention",
    };
  }

  return {
    id: "onTrack",
    label: "On track",
    message: "You’re in a good place for today",
    tone: "positive",
  };
};

export const getLoggedProteinSummary = (
  plan: Plan | undefined,
  dateKey: string,
): LoggedProteinSummary => {
  const dayEntry = getPlanDayEntryByDateKey(plan, dateKey);
  if (!dayEntry) {
    return { consumedProtein: 0, loggedMainMealCount: 0 };
  }

  let consumedProtein = 0;
  let loggedMainMealCount = 0;

  dayEntry.meals.forEach((mealGroup) => {
    const loggedMeals = mealGroup.data.filter((meal) => meal.isTickedOff === true);

    loggedMeals.forEach((meal) => {
      consumedProtein += meal.nutritionalInfo.perPortion?.protein?.grams ?? 0;
    });

    if (MAIN_MEAL_TIMES.has(mealGroup.mealTime) && loggedMeals.length > 0) {
      loggedMainMealCount += 1;
    }
  });

  (dayEntry.dayPlan.extraMeals ?? [])
    .filter((meal) => meal.isTickedOff === true)
    .forEach((meal) => {
      consumedProtein += meal.nutritionalInfo.perPortion?.protein?.grams ?? 0;
    });

  return { consumedProtein, loggedMainMealCount };
};
